import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const AD_CLIENT = 'ca-pub-3252607777266340';
const LOAD_MARGIN = '400px';
const SETTLE_DELAY = 600;

const RESERVED_HEIGHT = {
  display: { xs: 100, sm: 90 },
  inFeed: { xs: 200, sm: 250 },
  inArticle: { xs: 200, sm: 250 },
  multiplex: { xs: 300, sm: 350 },
};

const AdSense = ({ format = 'auto', slot = '2472453159', style = {}, className = '', type = 'display' }) => {
  const insRef = useRef(null);
  const pushed = useRef(false);

  const adStyle = {
    display: 'block',
    width: '100%',
    margin: '20px auto',
    ...style,
  };

  const adConfigs = {
    display: { format, style: adStyle },
    inFeed: { format: 'fluid', style: { ...adStyle, width: '100%', height: 'auto' } },
    inArticle: { format: 'fluid', style: { ...adStyle, width: '100%', height: 'auto', margin: '20px 0' } },
    multiplex: { format: 'auto', style: { ...adStyle, width: '100%', height: 'auto' } },
  };

  const config = adConfigs[type] || adConfigs.display;
  const reserved = RESERVED_HEIGHT[type] || RESERVED_HEIGHT.display;

  useEffect(() => {
    const el = insRef.current;
    if (!el) return undefined;

    let retryTimer;

    const request = () => {
      if (pushed.current) return true;
      if (el.getAttribute('data-adsbygoogle-status')) {
        pushed.current = true;
        return true;
      }
      if (el.offsetWidth === 0) return false;
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.warn('AdSense push failed', err);
        pushed.current = true;
      }
      return true;
    };

    const requestWithRetry = () => {
      if (request()) return;
      retryTimer = setTimeout(request, 300);
    };

    if (typeof window.IntersectionObserver !== 'function') {
      requestWithRetry();
      return () => clearTimeout(retryTimer);
    }

    let observer;
    const settleTimer = setTimeout(() => {
      observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            requestWithRetry();
            observer.disconnect();
          });
        },
        { rootMargin: LOAD_MARGIN }
      );
      observer.observe(el);
    }, SETTLE_DELAY);

    return () => {
      clearTimeout(settleTimer);
      clearTimeout(retryTimer);
      if (observer) observer.disconnect();
    };
  }, [slot]);

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        minHeight: { xs: `${reserved.xs}px`, sm: `${reserved.sm}px` },
      }}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={config.style}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slot}
        data-ad-format={config.format}
        data-full-width-responsive="true"
      />
    </Box>
  );
};

export default AdSense;

export const AD_FORMATS = {
  AUTO: 'auto',
  RECTANGLE: 'rectangle',
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
  FLUID: 'fluid'
};

export const AD_TYPES = {
  DISPLAY: 'display',
  IN_FEED: 'inFeed',
  IN_ARTICLE: 'inArticle',
  MULTIPLEX: 'multiplex'
};

export const AD_SLOTS = {
  DISPLAY_TOP: '2472453159',
  DISPLAY_MIDDLE: '4059762956',
  DISPLAY_BOTTOM: '9857501655',

  SIDEBAR: '8821135046',
  IN_ARTICLE: '9120517948',
  MULTIPLEX: '2362155011'
};
